import React, { useState, useRef, useEffect } from 'react';
import KanbanColumn from '../KanbanColumn/KanbanColumn';
import { ORDER_STATUSES } from '../../../constants/orderStatuses';
import { 
  calculateDropPosition, 
  calculateFractionalPosition, 
  showDropPlaceholder, 
  removeDropPlaceholder,
  insertCardAtPosition 
} from '../../../utils/dragAndDrop';
import './KanbanBoard.css';

const KanbanBoard = ({ 
  ordersByStatus, 
  onCardClick, 
  onStatusChange, 
  onOrderStatusUpdate 
}) => {
  const [draggedCard, setDraggedCard] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const boardRef = useRef(null);

  // Attach drag events to cards when they render
  useEffect(() => {
    const cards = boardRef.current?.querySelectorAll('.order-card');
    cards?.forEach(card => {
      card.addEventListener('dragstart', handleDragStart);
      card.addEventListener('dragend', handleDragEnd);
    });

    return () => {
      cards?.forEach(card => {
        card.removeEventListener('dragstart', handleDragStart);
        card.removeEventListener('dragend', handleDragEnd);
      });
    };
  }, [ordersByStatus]);

  const handleDragStart = (e) => {
    const card = e.currentTarget;
    const orderId = parseInt(card.dataset.orderId);
    const order = Object.values(ordersByStatus)
      .flat()
      .find(o => o.idPed === orderId);
    
    if (order) {
      setDraggedCard(order);
      card.classList.add('dragging');
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/html', card.outerHTML);
    }
  };

  const handleDragEnd = () => {
    setDraggedCard(null);
    setDragOverColumn(null);
    
    // Remove dragging class from all cards
    const cards = boardRef.current?.querySelectorAll('.order-card');
    cards?.forEach(card => {
      card.classList.remove('dragging');
    });
    
    // Remove drop zone highlighting and placeholders
    const columns = boardRef.current?.querySelectorAll('.column-content');
    columns?.forEach(column => {
      column.classList.remove('drag-over');
      removeDropPlaceholder(column);
    });
  };

  const handleDragOver = (e, status) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Show drop zone indicator
    if (draggedCard) {
      const column = e.currentTarget;
      const dropPosition = calculateDropPosition(e, column);
      showDropPlaceholder(column, dropPosition);
    }
  };

  const handleDragEnter = (e, status) => {
    e.preventDefault();
    setDragOverColumn(status);
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e) => {
    // Only remove the class if we're leaving the column entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      e.currentTarget.classList.remove('drag-over');
      removeDropPlaceholder(e.currentTarget);
      setDragOverColumn(null);
    }
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    removeDropPlaceholder(e.currentTarget);

    if (!draggedCard) return;

    const oldStatus = draggedCard.statusOperacao;
    const oldPosition = draggedCard.tokenPrioridade;
    const tenantId = draggedCard.tenantId;
    const pedidoId = draggedCard.idPed;

    try {
      // Calculate the position where the card should be inserted
      const dropPosition = calculateDropPosition(e, e.currentTarget);
      let newPosition = calculateFractionalPosition(dropPosition, e.currentTarget);

      const hasStatusChanged = oldStatus !== newStatus;
      const hasPositionChanged = oldPosition !== newPosition;

      if (hasStatusChanged || hasPositionChanged) {
        // Show loading state on the card
        const cardElement = e.currentTarget.querySelector(`[data-order-id="${pedidoId}"]`);
        if (cardElement) {
          const cardLoadingOverlay = cardElement.querySelector('.card-loading-overlay');
          if (cardLoadingOverlay) {
            cardLoadingOverlay.style.display = 'flex';
          }
          cardElement.style.pointerEvents = 'none';
          cardElement.classList.add('loading');
          cardElement.draggable = false;
        }

        // Call the API to update the order status
        await onOrderStatusUpdate({
          pedidoId,
          tenantId,
          status: newStatus,
          position: newPosition
        });

        // Hide loading state on success
        if (cardElement) {
          const cardLoadingOverlay = cardElement.querySelector('.card-loading-overlay');
          if (cardLoadingOverlay) {
            cardLoadingOverlay.style.display = 'none';
          }
          cardElement.style.pointerEvents = 'auto';
          cardElement.classList.remove('loading');
          cardElement.draggable = true;
        }
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Erro ao atualizar o status do pedido. Por favor, tente novamente.');
    }
  };

  return (
    <div className="kanban-board" ref={boardRef}>
      {Object.values(ORDER_STATUSES).map(status => (
        <KanbanColumn
          key={status}
          status={status}
          orders={ordersByStatus[status] || []}
          onCardClick={onCardClick}
          onStatusChange={onStatusChange}
          onDragOver={(e) => handleDragOver(e, status)}
          onDrop={(e) => handleDrop(e, status)}
          onDragEnter={(e) => handleDragEnter(e, status)}
          onDragLeave={handleDragLeave}
          isDragOver={dragOverColumn === status}
        />
      ))}
    </div>
  );
};

export default KanbanBoard;
